import React, { useState } from "react";
import Head from "next/head";
import Footer from "../footer";
import Header from "../header";
import Image, { ImageLoaderProps } from 'next/image';
import StoneityButton from "@/components/stoneity-button/stoneityButton";
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useAppSelector } from "@/redux/app/hooks";
import { isTryDemoViewableRedux } from "@/redux/slices/user-context";
import { useTranslation } from "next-i18next";
import { IMAGE_DELIVERY, imageLoader } from "@/base/utils/dataHelper";
import { useRouter } from "next/router";
import type { GetStaticProps, InferGetStaticPropsType } from "next";
import ServiceManagementPortal from "../images/serviceManagementPortal";
import ServiceManagementSelf from "../images/serviceManagementSelf";
import ServiceManagementAutomatic from "../images/serviceManagementAutomatic";
import ServiceManagementFollow from "../images/serviceManagementFollow";
import ServiceManagementData from "../images/serviceManagementData";
import ServiceManagementNoCode from "../images/serviceManagementNoCode";
import Link from "next/link";
import styles from "@/styles/ServiceManagement.module.css";
import "swiper/css";
import Demo from "@/components/demo";

enum ProductFeaturesEnum {
  Project,
  Task,
  ProjectView,
  Form
}
interface KeyAdvantages {
  id: number;
  icon: JSX.Element;
  title: string;
  description: string;
}

const ServiceManagementHomePage = (_props: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { t } = useTranslation('common');
  const router = useRouter();
  const isTryDemoViewable = useAppSelector(isTryDemoViewableRedux);
  const [productFeaturesTab, setProductFeaturesTab] = useState(ProductFeaturesEnum.Project)

  const isLanguage = router.locale?.toUpperCase().toString()

  const keyAdvantages: KeyAdvantages[] = [
    {
      id: 1,
      icon: <ServiceManagementPortal />,
      title: `${t("keyAdvantagesPortalTitle")}`,
      description: `${t("keyAdvantagesPortalDescription")}`,
    },
    {
      id: 2,
      icon: <ServiceManagementSelf />,
      title: `${t("keyAdvantagesSelfTitle")}`,
      description: `${t("keyAdvantagesSelfDescription")}`,
    },
    {
      id: 3,
      icon: <ServiceManagementAutomatic />,
      title: `${t("keyAdvantagesAutomaticTitle")}`,
      description: `${t("keyAdvantagesAutomaticDescription")}`,
    },
    {
      id: 4,
      icon: <ServiceManagementFollow />,
      title: `${t("keyAdvantagesFollowTitle")}`,
      description: `${t("keyAdvantagesFollowDescription")}`,
    },
    {
      id: 5,
      icon: <ServiceManagementData />,
      title: `${t("keyAdvantagesDataTitle")}`,
      description: `${t("keyAdvantagesDataDescription")}`,
    },
    {
      id: 6,
      icon: <ServiceManagementNoCode />,
      title: `${t("keyAdvantagesNoCodeTitle")}`,
      description: `${t("keyAdvantagesNoCodeDescription")}`,
    },
  ]

  const baseUrl = 'https://stoneity.com';
  const currentPath = router.asPath;
  const locale = router.locale || 'en';
  const canonicalUrl = locale === 'tr'
    ? `${baseUrl}/tr${currentPath}`
    : `${baseUrl}${currentPath}`;
  const alternateLocale = locale === 'tr' ? 'en' : 'tr';
  const alternatePath = `/service-management`;

  return (
    <>
      <Head>
        <title>{t('seo_service_management_title')}</title>
        <meta name="description" content={t('seo_service_management_description')} />
        <meta name="keywords" content="servis yönetimi, ITSM, müşteri desteği, teknik servis, servis portalı, Stoneity, service management, ITSM, customer support, service portal" />
        <link key="canonical" rel="canonical" href={canonicalUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={t('seo_service_management_title')} />
        <meta property="og:description" content={t('seo_service_management_description')} />
        <meta property="og:image" content={`${baseUrl}/stoneityLogo.png`} />
        <meta property="og:site_name" content="Stoneity" />
        <meta property="og:locale" content={locale === 'tr' ? 'tr_TR' : 'en_US'} />
        <meta property="og:locale:alternate" content={locale === 'tr' ? 'en_US' : 'tr_TR'} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={canonicalUrl} />
        <meta name="twitter:title" content={t('seo_service_management_title')} />
        <meta name="twitter:description" content={t('seo_service_management_description')} />
        <meta name="twitter:image" content={`${baseUrl}/stoneityLogo.png`} />
        <meta name="twitter:site" content="@stoneity" />

        {/* Alternate languages */}
        <link rel="alternate" hrefLang={locale} href={canonicalUrl} />
        <link rel="alternate" hrefLang={alternateLocale} href={`${baseUrl}/${alternateLocale}${alternatePath}`} />
        <link rel="alternate" hrefLang="x-default" href={`${baseUrl}${alternatePath}`} />
      </Head>
      <Header />
      <main className={styles.mainFlex}>
        <div className={styles.serviceManageBannerWrapper}>
          <div className={styles.serviceManageBannerContent}>
            <div className={styles.serviceManageBannerRight}>
              <div className={styles.serviceManageBannerTextContent}>
                <div className={styles.title}>{t("portalHomePageBannerText")}</div>
                <div className={styles.description}>{t("portalHomePageBannerExplain")}</div>
              </div>
              {isTryDemoViewable &&
                <StoneityButton buttonStyle="dark" buttonText={t('tryItForFree')} width={200} height={45} />
              }
            </div>
            <div className={styles.serviceManageBannerLeft}>
              <Image
                className={styles.serviceManageBannerImg}
                src={isLanguage === "TR" ? `${IMAGE_DELIVERY}/b6d19b56-af96-4e0b-010d-74ba97f38d00` : `${IMAGE_DELIVERY}/dfa6f9c5-c8e7-4a9d-bad3-12ef21f29800`}
                alt={"service-manage-banner.png"}
                style={{
                  height: "auto",
                  objectFit: "contain",
                  aspectRatio: "auto",
                  zIndex: 10,
                }}
                width={955}
                height={485}
                loader={(p: ImageLoaderProps) => imageLoader(p.src, 955, 485)}
              />
            </div>
          </div>
        </div>
        <div className={styles.pageFlex} style={{ backgroundColor: "#f6f6f6" }}>
          <div className={styles.brandWrapper}>
            <div className={styles.brandLeftWrapper}>
              <div className={styles.brandTitle}>{t("brandContentTitle")}</div>
              <div className={styles.brandExplain}>{t("brandContentExplain")}</div>
            </div>
            <Image
              src={isLanguage === "TR" ? `${IMAGE_DELIVERY}/ab6d2fc4-0216-45fa-cc29-f368f5c08000` : `${IMAGE_DELIVERY}/72498569-e579-453d-3552-02ef0a36a100`}
              alt=""
              width={560}
              height={591}
              style={{ objectFit: "contain", aspectRatio: "auto", height: "auto", flexShrink: "0" }}
              loader={(p: ImageLoaderProps) => imageLoader(p.src, 560, 591)}
            />
          </div>
        </div>
        <Demo t={t} />
        <div className={styles.pageFlex}>
          <div className={styles.keyAdvantagesTitle}>{t("keyAdvantagesTitle")}</div>
          <div className={styles.keyAdvantagesCardWrapper}>
            {keyAdvantages.map((keyAdvantages, index) => {
              return (
                <div
                  key={index}
                  className={styles.keyAdvantagesCard}>
                  <div className={styles.keyAdvantagesCardTop}>
                    {keyAdvantages.icon}
                    <div className={styles.keyAdvantagesCardTitle}>{keyAdvantages.title}</div>
                  </div>
                  <div className={styles.keyAdvantagesCardExplain}>{keyAdvantages.description}</div>
                </div>
              )
            })}
          </div>
        </div>
        <div className={styles.pageFlex}>
          <div className={styles.solutionsTitle}>{t("solutions")}</div>
          <div className={styles.productCardWrapper}>
            <div className={styles.productCard}>
              <Image
                className={styles.productCardImg}
                src={isLanguage === "TR" ? `${IMAGE_DELIVERY}/215bbc6d-f536-4fce-48b0-3c3bc4a2dd00` : `${IMAGE_DELIVERY}/14917aa4-667f-4d29-0b01-79d3d362bd00`}
                alt=""
                width={670}
                height={375}
                style={{ objectFit: "contain", aspectRatio: "auto", height: "auto" }}
                loader={(p: ImageLoaderProps) => imageLoader(p.src, 670, 375)}
              />
              <div className={styles.productCardTextContent}>
                <div className={styles.title}>{t("technicalServiceManagement")}</div>
                <div className={styles.text}>
                  <ul>
                    <li>{t("technicalServiceManagementDescriptionOne")}</li>
                    <li>{t("technicalServiceManagementDescriptionTwo")}</li>
                    <li>{t("technicalServiceManagementDescriptionThree")}</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className={styles.productCard}>
              <Image
                className={styles.productCardImg}
                src={isLanguage === "TR" ? `${IMAGE_DELIVERY}/f189a6a8-8af1-42fd-88bd-425dcd440500` : `${IMAGE_DELIVERY}/2d9dd9a0-2967-42b8-dcbf-409c0df11b00`}
                alt=""
                width={670}
                height={375}
                style={{ objectFit: "contain", aspectRatio: "auto", height: "auto", width: "100%" }}
                loader={(p: ImageLoaderProps) => imageLoader(p.src, 670, 375)}
              />
              <div className={styles.productCardTextContent}>
                <div className={styles.title}>{t("eCommerceSupportAfterSalesServices")}</div>
                <div className={styles.text}>
                  <ul>
                    <li>{t("eCommerceSupportAfterSalesServicesDescriptionOne")}</li>
                    <li>{t("eCommerceSupportAfterSalesServicesDescriptionTwo")}</li>
                    <li>{t("eCommerceSupportAfterSalesServicesDescriptionThree")}</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className={styles.productCard}>
              <Image
                className={styles.productCardImg}
                src={isLanguage === "TR" ? `${IMAGE_DELIVERY}/1bf05a4e-c30c-4cb5-0520-43ecf085ab00` : `${IMAGE_DELIVERY}/56207c75-f879-4a3f-b992-25687afd3e00`}
                alt=""
                width={670}
                height={375}
                style={{ objectFit: "contain", aspectRatio: "auto", height: "auto", width: "100%" }}
                loader={(p: ImageLoaderProps) => imageLoader(p.src, 670, 375)}
              />
              <div className={styles.productCardTextContent}>
                <div className={styles.title}>{t("itSupportServices")}</div>
                <div className={styles.text}>
                  <ul>
                    <li>{t("itSupportServicesDescriptionOne")}</li>
                    <li>{t("itSupportServicesDescriptionTwo")}</li>
                    <li>{t("itSupportServicesDescriptionThree")}</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className={styles.productCard}>
              <Image
                className={styles.productCardImg}
                src={isLanguage === "TR" ? `${IMAGE_DELIVERY}/6ef62019-02aa-47b2-c682-edf4e2522700` : `${IMAGE_DELIVERY}/ff06ff0c-ecdb-4f99-dd59-1ac5defb9b00`}
                alt=""
                width={670}
                height={375}
                style={{ objectFit: "contain", aspectRatio: "auto", height: "auto", width: "100%" }}
                loader={(p: ImageLoaderProps) => imageLoader(p.src, 670, 375)}
              />
              <div className={styles.productCardTextContent}>
                <div className={styles.title}>{t("humanResourcesSupportPortal")}</div>
                <div className={styles.text}>
                  <ul>
                    <li>{t("humanResourcesSupportPortalDescriptonOne")}</li>
                    <li>{t("humanResourcesSupportPortalDescriptonTwo")}</li>
                    <li>{t("humanResourcesSupportPortalDescriptonThree")}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.pageFlex} style={{ background: '#F6F9FF', position: 'relative', overflow: 'hidden' }}>
          <div className={styles.sloganWrapper}>
            <Image
              className={styles.sloganImg}
              src={`${IMAGE_DELIVERY}/6f6443e3-f2e0-4d29-7980-f8c854c1a500`}
              alt=""
              width={190}
              height={310}
              loader={(p: ImageLoaderProps) => imageLoader(p.src, 190, 310)}
            />
            <div>
              <Image
                className={styles.sloganImgResponsive}
                src={`${IMAGE_DELIVERY}/c49fab41-4d44-4421-6f2c-9a9108ffc900`}
                alt=""
                width={670}
                height={170}
                loader={(p: ImageLoaderProps) => imageLoader(p.src, 550, 550)}
              />
            </div>
            <div className={styles.sloganTextContent}>
              <div className={styles.title}>{t('teamProjectAndBusinessManagementPreference')} <span>{t('app_title')}</span></div>
              <div className={styles.buttonWrapper}>
                {isTryDemoViewable &&
                  <Link className={styles.link} href={'/try-demo'}>
                    <StoneityButton
                      buttonStyle="dark"
                      buttonText={t('tryItForFree')}
                      width={200}
                      height={45}
                    />
                  </Link>
                }
                <Link className={styles.link} href={'/service-management/plan'}>
                  <StoneityButton
                    buttonStyle="light"
                    buttonText={t('viewPlan')}
                    width={200}
                    height={45}
                  />
                </Link>
              </div>
            </div>
            <Image
              className={`${styles.sloganImg} ${styles.sloganImgRight}`}
              src={`${IMAGE_DELIVERY}/ab389015-63aa-4c00-a478-0f2568621900`}
              alt=""
              width={190}
              height={310}
              loader={(p: ImageLoaderProps) => imageLoader(p.src, 190, 310)}
            />
            <div>
              <Image
                className={styles.sloganImgResponsive}
                src={`${IMAGE_DELIVERY}/0a0029a2-cdcd-41c6-ef0b-588eac191b00`}
                alt=""
                width={670}
                height={170}
                loader={(p: ImageLoaderProps) => imageLoader(p.src, 550, 550)}
              />
            </div>
          </div>
        </div>
        <div className={styles.pageFlex}>
          <div className={styles.productCardWrapper}>
            <div className={styles.productCard}>
              <Image
                className={styles.productCardImg}
                src={isLanguage === "TR" ? `${IMAGE_DELIVERY}/237cfec7-cf85-4d80-4689-9f09d2a06900` : `${IMAGE_DELIVERY}/50993004-12cc-4844-1e34-3295d6cea800`}
                alt=""
                width={670}
                height={375}
                style={{ objectFit: "contain", aspectRatio: "auto", height: "auto", width: "100%", borderRadius: "15px 15px 0 0" }}
                loader={(p: ImageLoaderProps) => imageLoader(p.src, 670, 375)}
              />
              <div className={styles.productCardTextContent}>
                <div className={styles.title}>{t("vendorManagement")}</div>
                <div className={styles.text}>
                  <ul>
                    <li>{t("vendorManagementDescriptonOne")}</li>
                    <li>{t("vendorManagementDescriptonTwo")}</li>
                    <li>{t("vendorManagementDescriptonThree")}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* <div className={styles.pageFlex} style={{ background: '#F8F4EA' }}>
          <div className={styles.goalAndReportWrapper}>
            <div className={styles.title}>{t('goalsAndReports')}</div>
            <div className={styles.goalAndReportRow}>
              <div className={styles.goalAndReportBox}>
                <div className={styles.title}>{t('trackProjectProgress')}</div>
                <div className={styles.text}>{t('trackProjectProgressExplain')}</div>
                {isTryDemoViewable &&
                  <Link className={styles.link} href={'/try-demo'}>
                    <StoneityButton
                      buttonStyle="dark"
                      buttonText={t('tryItForFree')}
                      width={150}
                      height={40}
                      buttonClass="goalAndReportFreeButton"
                    />
                  </Link>
                }
              </div>
              <Image
                className={styles.goalAndReportFirstImg}
                src={isLanguage === "TR" ? `${IMAGE_DELIVERY}/f2128faf-97da-4893-5cb8-51d9e7428d00` : `${IMAGE_DELIVERY}/d59bcd47-96b6-4bc7-b02d-21642b34c500`}
                alt=""
                width={520}
                height={480}
                loader={(p: ImageLoaderProps) => imageLoader(p.src, 520, 480)}
              />
            </div>
            <div className={styles.goalAndReportRow}>
              <Image
                className={styles.goalAndReportSecondImg}
                src={isLanguage === "TR" ? `${IMAGE_DELIVERY}/27163c38-11af-44ab-b94a-c62b4a36c400` : `${IMAGE_DELIVERY}/c93627c3-a2e0-484d-956b-0a51b5545000`}
                alt=""
                width={450}
                height={450}
                loader={(p: ImageLoaderProps) => imageLoader(p.src, 450, 450)}
              />
              <div className={styles.goalAndReportBox}>
                <div className={styles.title}>{t('closelyMonitorCompanyGoals')}</div>
                <div className={styles.text}>{t('closelyMonitorCompanyGoalsExplain')}</div>
                {isTryDemoViewable &&
                  <Link className={styles.link} href={'/try-demo'}>
                    <StoneityButton
                      buttonStyle="dark"
                      buttonText={t('tryItForFree')}
                      width={150}
                      height={40}
                      buttonClass="goalAndReportFreeButton"
                    />
                  </Link>
                }
              </div>
            </div>
          </div>
        </div>
        <Promo /> */}
      </main>
      <Footer />
    </>
  );
}

export const getStaticProps: GetStaticProps = async ({ locale }) => ({
  props: {
    ...(await serverSideTranslations(locale || 'en', ['common'])),
  },
});

export default ServiceManagementHomePage;